import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Spinner from "@components/Spinner";
import { useChatLauncherClearance } from "@components/ChatwootLauncher";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import { useInView } from "@src/hooks/useInView";
import type { TNdviDateItem } from "@services/fieldsService";

// `compact` is the compare pane's smaller strip; it is a styling prop only.
const styleOnly = { shouldForwardProp: (p: string) => p !== "compact" };

const Root = styled("div", styleOnly)<{ compact?: boolean }>`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  z-index: 2;
  ${({ compact }) =>
    compact
      ? `
  bottom: 12px;
  left: 12px;
  right: 12px;
  border-radius: 8px;
  padding: 6px 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
`
      : `
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 10px;
  padding: 8px 4px;
  max-width: calc(100% - 32px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    bottom: 12px;
    max-width: calc(100% - 16px);
  }
`}
`;

const Strip = styled("div", styleOnly)<{ compact?: boolean }>`
  display: flex;
  gap: ${({ compact }) => (compact ? 6 : 8)}px;
  overflow-x: auto;
  padding: 0 ${({ compact }) => (compact ? 2 : 4)}px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: ${({ compact }) => (compact ? 3 : 4)}px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`;

const DateCard = styled("button", styleOnly)<{ active?: boolean; compact?: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ compact }) => (compact ? 3 : 4)}px;
  padding: ${({ compact }) => (compact ? 3 : 4)}px;
  border: 2px solid ${({ active }) => (active ? "#68ad18" : "transparent")};
  border-radius: ${({ compact }) => (compact ? 6 : 8)}px;
  background: ${({ active }) => (active ? "rgba(104, 173, 24, 0.08)" : "transparent")};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ active }) => (active ? "#68ad18" : "#ccc")};
  }
`;

const Thumbnail = styled("div", styleOnly)<{ compact?: boolean }>`
  width: ${({ compact }) => (compact ? 52 : 64)}px;
  height: ${({ compact }) => (compact ? 38 : 48)}px;
  border-radius: ${({ compact }) => (compact ? 3 : 4)}px;
  overflow: hidden;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CloudBadge = styled("span", styleOnly)<{ high?: boolean; compact?: boolean }>`
  position: absolute;
  top: ${({ compact }) => (compact ? 1 : 2)}px;
  right: ${({ compact }) => (compact ? 1 : 2)}px;
  font-size: ${({ compact }) => (compact ? 8 : 9)}px;
  font-weight: 700;
  font-family: Nunito Sans, sans-serif;
  background: ${({ high }) => (high ? "rgba(255,80,80,0.85)" : "rgba(0,0,0,0.55)")};
  color: #fff;
  padding: 1px ${({ compact }) => (compact ? 3 : 4)}px;
  border-radius: ${({ compact }) => (compact ? 2 : 3)}px;
  line-height: 1.2;
`;

export const DateLabel = styled("span", styleOnly)<{ compact?: boolean }>`
  font-family: Nunito Sans, sans-serif;
  font-size: ${({ compact }) => (compact ? 10 : 11)}px;
  font-weight: 600;
  color: #494c5b;
  white-space: nowrap;
`;

const LoadMore = styled("button", styleOnly)<{ compact?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ compact }) => (compact ? "6px 12px" : "8px 16px")};
  border: 1px dashed #ccc;
  border-radius: ${({ compact }) => (compact ? 6 : 8)}px;
  background: transparent;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: ${({ compact }) => (compact ? 11 : 12)}px;
  font-weight: 600;
  color: #68ad18;
  white-space: nowrap;

  &:hover {
    background: rgba(104, 173, 24, 0.06);
  }
`;

const SubscriptionBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 4px 6px;
  padding: 5px 10px;
  border-radius: 6px;
  background: rgba(104, 173, 24, 0.12);
  color: #3f6b0d;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
`;

// Fills the Thumbnail box so the observer always has a laid-out element to
// watch, and so cards keep their size before their preview arrives.
const ThumbnailSlot = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailError = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 9px;
  font-weight: 600;
  color: #9a9da8;
`;

/**
 * Each preview is one Sentinel Hub request against a capped monthly quota, so a
 * card fetches only once it is actually scrolled near — see useInView.
 */
const ThumbnailImage: React.FC<{
  date: string;
  layer: string;
  getThumbnail: (date: string) => Promise<string>;
  rootRef: React.RefObject<HTMLElement>;
  compact?: boolean;
}> = ({ date, layer, getThumbnail, rootRef, compact }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const { ref: cardRef, inView } = useInView<HTMLDivElement>({ root: rootRef });
  // The caller builds getThumbnail on every render; reading it through a ref
  // keeps it out of the effect's deps so a re-render never refetches. A new
  // field remounts the strip instead (callers key it by field).
  const getThumbnailRef = useRef(getThumbnail);
  getThumbnailRef.current = getThumbnail;

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    setFailed(false);
    setSrc(null);
    getThumbnailRef.current(date)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
    // `layer` is a dep so the thumbnail refetches when the spectral layer changes.
  }, [date, layer, inView]);

  // The ref has to live on an element that is always rendered, or the observer
  // has nothing to watch and the card never loads.
  return (
    <ThumbnailSlot ref={cardRef}>
      {failed ? (
        <ThumbnailError>no image</ThumbnailError>
      ) : src ? (
        <img src={src} alt={`${layer} ${date}`} />
      ) : inView ? (
        <Spinner size={compact ? 12 : 16} />
      ) : null}
    </ThumbnailSlot>
  );
};

export const formatDate = (iso: string): string => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * The spectral (NDVI / brightness / moisture) date strip, presentational.
 * Rendered by the main map (NdviDateStrip, below) and, `compact`, by the
 * compare pane — which used to carry its own copy of every piece of it.
 */
export const NdviDateStripView: React.FC<{
  dates: TNdviDateItem[];
  selectedDate: string | null;
  onSelect: (date: string) => void;
  /** One card's preview (a billed Sentinel Hub request; see ThumbnailImage). */
  getThumbnail: (date: string) => Promise<string>;
  /** The spectral layer on screen — a change refetches the previews. */
  layer: string;
  hasMore: boolean;
  onLoadMore: () => void;
  /** First page still loading: a spinner instead of the strip. */
  loading?: boolean;
  banner?: React.ReactNode;
  compact?: boolean;
}> = ({ dates, selectedDate, onSelect, getThumbnail, layer, hasMore, onLoadMore, loading, banner, compact }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  useChatLauncherClearance(rootRef);
  return (
    <Root ref={rootRef} compact={compact}>
      {banner}
      {loading && dates.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
          <Spinner size={20} />
        </div>
      ) : (
        <Strip ref={stripRef} compact={compact}>
          {dates.map((item) => (
            <DateCard
              key={item.date}
              active={selectedDate === item.date}
              compact={compact}
              onClick={() => onSelect(item.date)}
            >
              <Thumbnail compact={compact}>
                <ThumbnailImage
                  date={item.date}
                  layer={layer}
                  getThumbnail={getThumbnail}
                  rootRef={stripRef}
                  compact={compact}
                />
                <CloudBadge high={item.cloudCover > 50} compact={compact}>
                  {Math.round(item.cloudCover)}%
                </CloudBadge>
              </Thumbnail>
              <DateLabel compact={compact}>{formatDate(item.date)}</DateLabel>
            </DateCard>
          ))}
          {hasMore && (
            <LoadMore compact={compact} onClick={onLoadMore}>
              {compact ? "More" : "Load more"}
            </LoadMore>
          )}
        </Strip>
      )}
    </Root>
  );
};

const NdviDateStrip: React.FC = () => {
  const vm = useFieldsScreenVM();
  const { accountStore } = useStores();
  const lang = useLang();

  if (!vm.isSpectralView(vm.selectedFieldView) || vm.selectedFieldId == null) return null;
  if (vm.ndviDates.length === 0 && !vm.ndviLoading) return null;

  const fieldId = String(vm.selectedFieldId);
  // Backend already limits unpaid users to the recent window; this banner explains why
  // older imagery isn't listed.
  const restricted = !accountStore.subscriptionPaid;

  return (
    <NdviDateStripView
      key={fieldId}
      dates={vm.ndviDates}
      selectedDate={vm.ndviSelectedDate}
      onSelect={(date) => vm.setNdviSelectedDate(date)}
      getThumbnail={(date) => vm.getNdviThumbnail(fieldId, date, vm.spectralLayer)}
      layer={vm.spectralLayer}
      hasMore={vm.ndviDates.length < vm.ndviTotal}
      onLoadMore={() => vm.loadMoreNdviDates()}
      loading={vm.ndviLoading}
      banner={
        restricted ? <SubscriptionBanner>{lang.subscription.ndviBanner}</SubscriptionBanner> : undefined
      }
    />
  );
};

export default observer(NdviDateStrip);
